<template>
  <zm-dialog
    class="opc-plan-selection-dialog"
    :class="{
      'opc-modal-dialog': isModal
    }"
    v-model="visible"
    :show-close="!isModal"
    title="Select a plan"
    width="920px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <template #header>
      <div class="plan-selection-dialog-header">
        <div class="plan-selection-dialog-header-image" v-if="imagePath">
          <img :src="imagePath" alt="Upgrade plan" />
          <div></div>
        </div>
        <div class="plan-selection-dialog-header-content" v-if="title || description">
          <h3 v-if="title">{{ title }}</h3>
          <p v-if="description">{{ description }}</p>
          <!-- <zm-link v-if="learnMoreUrl" :href="learnMoreUrl" target="_blank" show-icon>{{ $t('billing.common.Learn_more') }}</zm-link> -->
        </div>
      </div>
    </template>
    <div
      class="plan-selection-dialog-content"
      :style="{ gridTemplateColumns: `repeat(${dialogCardColumns}, minmax(0, 1fr))` }"
    >
      <plan-card
        v-for="(plan, index) in plans"
        :key="plan.id"
        :plan="plan"
        :loading-id="loadingId"
        :is-middle="index != 0 && index != plans.length - 1"
        :is-popular="plan.isPopular"
        :is-current="plan.isCurrent"
        @buyNow="() => handleBuyNow(plan)"
        @toggleHighlights="(action) => handleToggleHighlights(plan, action)"
      />
    </div>
  </zm-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import PlanCard from '@/components/plan-card.vue';
import { BASE_PLAN_SUB_TYPES, MODAL_ENTRY_SOURCE_MAP } from '@/common/constants';

import {
  getSchedulerPlanSelectionConfigs,
  getClipsPlanSelectionConfigs,
  PLAN_SELECTION_ID,
  PLAN_SELECTION_TRACKING_MAP,
} from './plan-selection-configs';
import { PLAN_SELECTION_IMAGE_MAP } from './constants';
import { MODAL_CHECKOUT_MESSAGE_HOOK_MAP } from '@/utils/modal-utils.js';
import { shouldUseCrossOrigin } from '@/utils/crossOriginHelper';

/**
 * NOTE: from page and corresponding unified link:
 * 
 * from 'scheduler': /buy?product=ZSCHED
 * from 'clips':     /buy?product=PRO
 */
export default {
  name: 'PlanSelectionDialog',
  components: {
    PlanCard
  },
  props: {
    from: {
      type: String,
      required: true,
    },
    entry: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      visible: false,
      loadingId: null,
    };
  },
  computed: {
    ...mapGetters(['boughtPlans']),
    isModal() {
      return this.$context?.is?.modal;
    },
    currentUserPlanType() {
      const boughtPlans = Array.isArray(this.boughtPlans) ? this.boughtPlans : [];
      if (this.from === 'scheduler') {
        const isProPlan = boughtPlans.some((plan = {}) => (
          plan.category === BASE_PLAN_SUB_TYPES.PRO ||
          plan.sub_type === BASE_PLAN_SUB_TYPES.PRO ||
          plan?.extra?.sub_type === BASE_PLAN_SUB_TYPES.PRO
        ));
        if (isProPlan) {
          return 'pro';
        }

        const isProPlusPlan = boughtPlans.some((plan = {}) => (
          plan.category === BASE_PLAN_SUB_TYPES.ZOOM_ONE_PRO_PLUS ||
          plan.sub_type === BASE_PLAN_SUB_TYPES.ZOOM_ONE_PRO_PLUS ||
          plan?.extra?.sub_type === BASE_PLAN_SUB_TYPES.ZOOM_ONE_PRO_PLUS
        ));
        if (isProPlusPlan) {
          return 'pro_plus';
        }

        return 'free';
      }

      if (this.from === 'clips') {
        // Clips only promote Pro plan
        return 'pro'
      }
      return 'other';
    },
    plans() {
      if (this.currentUserPlanType === 'other') {
        return [];
      }
      if (this.from === 'scheduler') {
        return getSchedulerPlanSelectionConfigs(this.currentUserPlanType);
      }
      if (this.from === 'clips') {
        return getClipsPlanSelectionConfigs(this.currentUserPlanType);
      }
      return [];
    },
    imagePath() {
      if (this.from === 'scheduler') {
        switch (this.entry) {
          case MODAL_ENTRY_SOURCE_MAP.WIDGET:
            return PLAN_SELECTION_IMAGE_MAP.SCHEDULER_WIDGET;
          case MODAL_ENTRY_SOURCE_MAP.NOTIFICATION:
            return PLAN_SELECTION_IMAGE_MAP.SCHEDULER_NOTIFICATION;
          case MODAL_ENTRY_SOURCE_MAP.CREATE_TEAMS:
            return PLAN_SELECTION_IMAGE_MAP.SCHEDULER_CREATE_TEAMS;
          case MODAL_ENTRY_SOURCE_MAP.BOOK_GROUPS:
            return PLAN_SELECTION_IMAGE_MAP.SCHEDULER_BOOK_GROUPS;
          case MODAL_ENTRY_SOURCE_MAP.ACROSS_TEAMS:
            return PLAN_SELECTION_IMAGE_MAP.SCHEDULER_ACROSS_TEAMS;
          case MODAL_ENTRY_SOURCE_MAP.TEMPLATES:
            return PLAN_SELECTION_IMAGE_MAP.SCHEDULER_TEMPLATES;
          case MODAL_ENTRY_SOURCE_MAP.PREMIUM:
            return PLAN_SELECTION_IMAGE_MAP.SCHEDULER_PREMIUM;
          case MODAL_ENTRY_SOURCE_MAP.CALENDARS:
            return PLAN_SELECTION_IMAGE_MAP.SCHEDULER_CALENDARS;
          case MODAL_ENTRY_SOURCE_MAP.CREATE_GROUPS:
            return PLAN_SELECTION_IMAGE_MAP.SCHEDULER_CREATE_GROUPS;
          case MODAL_ENTRY_SOURCE_MAP.REMOVE_BRANDING:
            return PLAN_SELECTION_IMAGE_MAP.SCHEDULER_REMOVE_BRANDING;
          case MODAL_ENTRY_SOURCE_MAP.ADD_LOGO:
            return PLAN_SELECTION_IMAGE_MAP.SCHEDULER_ADD_LOGO;
          case MODAL_ENTRY_SOURCE_MAP.CUSTOM_EMAIL_STYLE:
            return PLAN_SELECTION_IMAGE_MAP.SCHEDULER_CUSTOM_EMAIL_STYLE;
          default:
            return PLAN_SELECTION_IMAGE_MAP.SCHEDULER_WIDGET
        }
      } else if (this.from === 'clips') {
        return PLAN_SELECTION_IMAGE_MAP.CLIPS_WIDGET;
      }

      return '';
    },
    title() {
      if (this.from === 'scheduler') {
        switch (this.entry) {
          case MODAL_ENTRY_SOURCE_MAP.WIDGET:
            return this.$t('planSelectionDialog.scheduler.widget.title');
          case MODAL_ENTRY_SOURCE_MAP.NOTIFICATION:
            return this.$t('planSelectionDialog.scheduler.notification.title');
          case MODAL_ENTRY_SOURCE_MAP.CREATE_TEAMS:
            return this.$t('planSelectionDialog.scheduler.createTeams.title');
          case MODAL_ENTRY_SOURCE_MAP.BOOK_GROUPS:
            return this.$t('planSelectionDialog.scheduler.bookGroups.title');
          case MODAL_ENTRY_SOURCE_MAP.ACROSS_TEAMS:
            return this.$t('planSelectionDialog.scheduler.acrossTeams.title');
          case MODAL_ENTRY_SOURCE_MAP.TEMPLATES:
            return this.$t('planSelectionDialog.scheduler.templates.title');
          case MODAL_ENTRY_SOURCE_MAP.PREMIUM:
            return this.$t('planSelectionDialog.scheduler.premium.title');
          case MODAL_ENTRY_SOURCE_MAP.CALENDARS:
            return this.$t('planSelectionDialog.scheduler.calendars.title');
          case MODAL_ENTRY_SOURCE_MAP.CREATE_GROUPS:
            return this.$t('planSelectionDialog.scheduler.createGroups.title');
          case MODAL_ENTRY_SOURCE_MAP.REMOVE_BRANDING:
            return this.$t('planSelectionDialog.scheduler.removeBranding.title');
          case MODAL_ENTRY_SOURCE_MAP.ADD_LOGO:
            return this.$t('planSelectionDialog.scheduler.addLogo.title');
          case MODAL_ENTRY_SOURCE_MAP.CUSTOM_EMAIL_STYLE:
            return this.$t('planSelectionDialog.scheduler.customEmailStyle.title');
          default:
            return this.$t('planSelectionDialog.scheduler.widget.title');
        }
      } else if (this.from === 'clips') {
        return this.$t('planSelectionDialog.clips.widget.title');
      }
      return '';
    },
    description() {
      if (this.from === 'scheduler') {
        switch (this.entry) {
          case MODAL_ENTRY_SOURCE_MAP.WIDGET:
            return this.$t('planSelectionDialog.scheduler.widget.description');
          case MODAL_ENTRY_SOURCE_MAP.NOTIFICATION:
            return this.$t('planSelectionDialog.scheduler.notification.description');
          case MODAL_ENTRY_SOURCE_MAP.CREATE_TEAMS:
            return this.$t('planSelectionDialog.scheduler.createTeams.description');
          case MODAL_ENTRY_SOURCE_MAP.BOOK_GROUPS:
            return this.$t('planSelectionDialog.scheduler.bookGroups.description');
          case MODAL_ENTRY_SOURCE_MAP.ACROSS_TEAMS:
            return this.$t('planSelectionDialog.scheduler.acrossTeams.description');
          case MODAL_ENTRY_SOURCE_MAP.TEMPLATES:
            return this.$t('planSelectionDialog.scheduler.templates.description');
          case MODAL_ENTRY_SOURCE_MAP.PREMIUM:
            return this.$t('planSelectionDialog.scheduler.premium.description');
          case MODAL_ENTRY_SOURCE_MAP.CALENDARS:
            return this.$t('planSelectionDialog.scheduler.calendars.description');
          case MODAL_ENTRY_SOURCE_MAP.CREATE_GROUPS:
            return this.$t('planSelectionDialog.scheduler.createGroups.description');
          case MODAL_ENTRY_SOURCE_MAP.REMOVE_BRANDING:
            return this.$t('planSelectionDialog.scheduler.removeBranding.description');
          case MODAL_ENTRY_SOURCE_MAP.ADD_LOGO:
            return this.$t('planSelectionDialog.scheduler.addLogo.description');
          case MODAL_ENTRY_SOURCE_MAP.CUSTOM_EMAIL_STYLE:
            return this.$t('planSelectionDialog.scheduler.customEmailStyle.description');
          default:
            return this.$t('planSelectionDialog.scheduler.widget.description');
        }
      } else if (this.from === 'clips') {
        return this.$t('planSelectionDialog.clips.widget.description');
      }
      return '';
    },
    // learnMoreUrl() {
    //   if (this.from === 'scheduler') {
    //     return 'https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0084722';
    //   }
    //   return '';
    // },
    dialogCardColumns() {
      const len = this.plans?.length || 0;
      if (len <= 1) {
        return 1;
      }
      return len >= 3 ? 3 : 2;
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        try {
          const messageTarget = shouldUseCrossOrigin() ? window : window.top;
          if (val) {
            this.$tracking.buyInteraction(`zoom ${this.from} - standalone in-product web modal checkout`, 'select a product modal - impression');
            messageTarget.addEventListener('message', this.handleMessage);
          } else {
            messageTarget.removeEventListener('message', this.handleMessage);
          }
        } catch (error) {
          console.error('Error setting up message listener:', error);
        }
      }
    }
  },
  methods: {
    handleMessage (event) {
      if (event?.data?.type === MODAL_CHECKOUT_MESSAGE_HOOK_MAP.sendClose) {
        this.$tracking.buyInteraction(`zoom ${this.from} - standalone in-product web modal checkout`, 'select a product modal - close');
      }
    },
    closeDialog() {
      this.visible = false;
    },
    handleClose() {
      this.closeDialog();
      this.$emit('close');
    },
    handleBuyNow(plan) {
      const callback = plan?.btnCallback;
      if (typeof callback !== 'function') {
        return;
      }
      const trackingPlanName = PLAN_SELECTION_TRACKING_MAP?.[plan?.id];
      if (trackingPlanName) {
        const action = plan?.id === PLAN_SELECTION_ID.SCHEDULER_PLAN ? 'buy now' : 'upgrade';
        this.$tracking.buyInteraction(
          `zoom ${this.from} - standalone in-product web modal checkout`,
          `select a product modal - ${trackingPlanName} - ${action}`,
        );
      }
      // Keep callback args explicit so config-level handlers can close the dialog safely.
      callback({
        closeDialog: this.closeDialog,
        plan,
        from: this.from,
        entry: this.entry,
      });
    },
    handleToggleHighlights(plan, action) {
      const trackingPlanName = PLAN_SELECTION_TRACKING_MAP?.[plan?.id];
      if (trackingPlanName) {
        if (action === 'expand') {
          this.$tracking.buyInteraction(`zoom ${this.from} - standalone in-product web modal checkout`, `select a product modal - ${trackingPlanName} - see more highlights`);
        } else if (action === 'collapse') {
          this.$tracking.buyInteraction(`zoom ${this.from} - standalone in-product web modal checkout`, `select a product modal - ${trackingPlanName} - see less highlights`);
        }
      }
    },
  }
};
</script>

<style lang="scss">
@import "@/styles/modal.scss";
@import "@/styles/variables.scss";

.opc-plan-selection-dialog {
  border: 0;
  height: 800px;
  overflow-y: auto;

  &.opc-modal-dialog.zbo-dialog {
    padding: 0 !important;
  }

  .zbo-dialog__header {
    padding: 0;
  }
  .plan-selection-dialog-header {
    display: flex;
    flex-direction: column;
    &-image {
      height: 240px;
      background: linear-gradient(180deg, rgba(13, 107, 222, 0.20) 0%, rgba(13, 107, 222, 0.00) 100%), var(--fill-fill-default, #FFF);

      img {
        height: 100%;
        width: 100%;
        object-fit: contain;
      }
    }
    &-content {
      padding: 24px;
      background: var(--zoom-color-fill-default, #FFFFFF);
      h3 {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        line-height: 28px;
        color: var(--zoom-color-text-stronger-neutral, #222325);
      }
      p {
        margin: 8px 0;
        font-size: 14px;
        line-height: 20px;
        color: var(--zoom-color-text-stronger-neutral, #222325);
      }
    }
  }
  .zbo-dialog__close.zm-icon-close {
    font-size: 14px;
  }
  .zbo-dialog {
    border-radius: 16px;
  }
  .plan-selection-dialog-content {
    padding-top: 28px;
    display: grid;
    justify-content: center;
    gap: 0;
  }
}
</style>